import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { apiErrorMessage, fetchFx, saveFxPin, type FxCurrencyState, type FxSettingsResponse } from "../../expenses/services/expensesService";
import { Chip, ErrorBox, Field, Notice, Page, Spinner } from "../../expenses/components/ui";
import { dateTime } from "../../expenses/lib/format";
import { ui } from "../../expenses/theme";

// Tipo de cambio de Finanzas internas (#1255), a pesos. Por omisión se usa el
// oficial del día; aquí se puede FIJAR un valor desde una fecha (hoy o futura).
// Nunca es retroactivo: lo ya capturado conserva su tipo de cambio y los meses
// pasados se siguen leyendo con lo que valía entonces.

const rateText = (rate: number | null | undefined, language: string) =>
  rate == null ? "—" : new Intl.NumberFormat(language.startsWith("en") ? "en-US" : "es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(rate);

function CurrencyCard({ state, base, today, canEdit, busy, onPin }: { state: FxCurrencyState; base: string; today: string; canEdit: boolean; busy: boolean; onPin: (rate: number | null, from: string) => void }) {
  const { t, i18n } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const tf = (k: string, o?: Record<string, unknown>) => t(`financeSettings.fx.${k}`, o);
  const [rate, setRate] = useState("");
  const [from, setFrom] = useState(today);
  const valid = Number(rate) > 0 && /^\d{4}-\d{2}-\d{2}$/.test(from) && from >= today;

  return (
    <div className={c.card} data-testid={`fx-${state.currency}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className={`text-xs font-medium uppercase tracking-wider ${c.muted}`}>{tf("pair", { currency: state.currency, base })}</p>
          <p className={`mt-1 text-3xl font-bold tabular-nums ${c.text}`} data-testid={`fx-effective-${state.currency}`}>
            {state.effective ? `$${rateText(state.effective.rate, i18n.language)}` : "—"}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {state.pinned ? (
              <Chip className="bg-amber-500/15 text-amber-600">
                <i className="ri-pushpin-line mr-1" />
                {tf("pinnedSince", { date: state.pinned.from })}
              </Chip>
            ) : (
              <Chip className="bg-teal-500/15 text-teal-600">
                <i className="ri-refresh-line mr-1" />
                {tf("automatic")}
              </Chip>
            )}
            <span className={`text-xs ${c.muted}`}>
              {state.official ? tf("official", { rate: rateText(state.official.rate, i18n.language), source: tf(`source.${state.official.source || "otro"}`), date: state.official.date || today }) : tf("officialUnavailable")}
            </span>
          </div>
        </div>
        {canEdit && state.pinned && (
          <button type="button" className={c.secondary} disabled={busy} onClick={() => onPin(null, today)}>
            <i className="ri-refresh-line mr-1" />
            {tf("release")}
          </button>
        )}
      </div>

      {canEdit && (
        <div className="mt-4 grid grid-cols-1 items-end gap-3 sm:grid-cols-4">
          <Field label={tf("newRate", { currency: state.currency })}>
            <input type="number" min="0" step="0.0001" aria-label={`rate-${state.currency}`} className={c.input} value={rate} onChange={(e) => setRate(e.target.value)} placeholder={state.official ? String(state.official.rate) : "0.00"} />
          </Field>
          <Field label={tf("from")}>
            <input type="date" aria-label={`from-${state.currency}`} className={c.input} value={from} min={today} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <div className="sm:col-span-2">
            <button
              type="button"
              className={c.primary}
              disabled={busy || !valid}
              onClick={() => {
                onPin(Number(rate), from);
                setRate("");
              }}
            >
              <i className="ri-pushpin-line mr-1" />
              {tf("pin")}
            </button>
            <p className={`mt-1 text-xs ${c.muted}`}>{tf("pinHint")}</p>
          </div>
        </div>
      )}

      {state.history.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <p className={`mb-1 text-xs font-semibold ${c.text}`}>{tf("history")}</p>
          <table className="w-full min-w-[480px] text-sm" aria-label={`${tf("history")} ${state.currency}`}>
            <thead>
              <tr className={`text-left text-xs ${c.muted}`}>
                <th className="py-1">{tf("from")}</th>
                <th className="py-1 text-right">{tf("value")}</th>
                <th className="py-1 pl-3">{tf("by")}</th>
                <th className="py-1" />
              </tr>
            </thead>
            <tbody>
              {state.history.map((h) => (
                <tr key={`${h.from}-${h.at || ""}`} className={`border-t ${c.border} ${c.text}`}>
                  <td className="py-1.5 tabular-nums">{h.from}</td>
                  <td className="py-1.5 text-right tabular-nums">{h.rate == null ? tf("backToOfficial") : `$${rateText(h.rate, i18n.language)}`}</td>
                  <td className={`py-1.5 pl-3 text-xs ${c.muted}`}>
                    {h.by || "—"}
                    {h.at ? ` · ${dateTime(h.at, i18n.language)}` : ""}
                  </td>
                  <td className="py-1.5 text-right">{h.upcoming && <Chip className="bg-indigo-500/15 text-indigo-600">{tf("upcoming")}</Chip>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function FxSection() {
  const { t } = useTranslation("common");
  const tf = (k: string, o?: Record<string, unknown>) => t(`financeSettings.fx.${k}`, o);
  const [data, setData] = useState<FxSettingsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await fetchFx());
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err) || t("financeSettings.errors.load"));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const pin = async (currency: string, rate: number | null, from: string) => {
    setBusy(true);
    setNotice(null);
    try {
      setData(await saveFxPin({ currency, rate, from }));
      setNotice(rate == null ? tf("released", { currency }) : tf("pinnedOk", { currency, date: from }));
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err) || t("financeSettings.errors.save"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page title={tf("title")} subtitle={tf("subtitle")}>
      <ErrorBox message={error} />
      {notice && <Notice tone="ok">{notice}</Notice>}
      <Notice tone="info">{tf("rule")}</Notice>
      {data && !data.canEdit && <Notice tone="info">{t("financeSettings.readOnly")}</Notice>}
      {!data && !error && <Spinner />}
      {data?.currencies.map((cur) => (
        <CurrencyCard key={cur.currency} state={cur} base={data.base} today={data.today} canEdit={data.canEdit} busy={busy} onPin={(rate, from) => void pin(cur.currency, rate, from)} />
      ))}
    </Page>
  );
}
